<template>
  <div class="yk-message-core">
    <div ref="scrollBody" class="yk-message-core__body" @scroll="handleScroll">
      <div v-if="upLoading" class="yk-message-core__tip">加载中...</div>
      <div v-else-if="!hasMoreUp && messageList.length" class="yk-message-core__tip">
        没有更早消息了
      </div>
      <div
        v-for="messageItem in messageListRepeat"
        :key="messageItem.id"
        :ref="`messageItem-${messageItem.id}`"
        class="yk-message-core__item"
        :class="{
          'is-self': messageItem.isSelf,
          'is-located': messageItem.id === locatedMessageId,
          'is-center-message': isCenterMessage(messageItem)
        }"
      >
        <el-image class="yk-message-core__avatar" :src="messageItem.avatar">
          <template #error>
            <el-image :src="defaultAvatar"></el-image>
          </template>
        </el-image>
        <div class="yk-message-core__content">
          <div class="yk-message-core__meta">
            <div class="yk-message-core__sender">
              <span class="yk-message-core__sender-name">{{ messageItem.senderName }}</span>
            </div>
            <div class="yk-message-core__time">
              <span>{{ messageItem.date }}</span>
              <span>{{ messageItem.time }}</span>
            </div>
          </div>
          <div class="yk-message-core__bubble">
            <message-type
              :data-item="messageItem"
              :is-self="messageItem.isSelf"
              :message="messageItem.msgContent"
              :message-type="messageItem.type"
            />
          </div>
        </div>
      </div>
      <div v-if="downLoading" class="yk-message-core__tip">加载中...</div>
      <div v-else-if="!hasMoreDown && messageList.length" class="yk-message-core__tip">
        没有更新消息了
      </div>
      <div v-if="!messageList.length && !upLoading && !downLoading" class="yk-message-core__empty">
        暂无聊天记录
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'YkMessageCore'
}
</script>

<script setup>
import { computed, getCurrentInstance, nextTick, ref, watch } from 'vue'
import MessageType from './components/message-type/index.vue'
import { getGroupWxChatRecord, getSingleWxChatRecord } from '@/api/yunke'
import { ENUM_MESSAGE_TYPE } from './enum.yk-message-core'

const props = defineProps({
  source: {
    type: String,
    default: 'person'
  },
  params: {
    type: Object,
    default: () => ({
      wechatUserId: '',
      wechatId: '',
      loginUserId: '',
      // 群
      roomId: ''
    })
  },
  pageSize: {
    type: Number,
    default: 15
  },
  filter: {
    type: Object,
    default: () => ({})
  }
})

const { proxy } = getCurrentInstance()
const scrollBody = ref(null)
const messageList = ref([])
const hasMoreUp = ref(true)
const hasMoreDown = ref(false)
const upLoading = ref(false)
const downLoading = ref(false)
const locatedMessageId = ref('')
const defaultAvatar = require('@/assets/user-default.png')

const messageParams = computed(() => {
  const params = props.params || {}

  return {
    wechatUserId: params.wechatUserId,
    wechatId: params.wechatId,
    loginUserId: params.loginUserId,
    roomId: params.roomId || ''
  }
})

// 兼容后端做去重
const messageListRepeat = computed(() => {
  const messageIdMap = {}

  return messageList.value.filter((messageItem) => {
    const messageId = messageItem && messageItem.id
    if (!messageId) return true
    if (messageIdMap[messageId]) return false

    messageIdMap[messageId] = true
    return true
  })
})

const formatDate = (date) => {
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${date.getFullYear()}-${month}-${day}`
}

const formatTime = (date) => {
  const hour = String(date.getHours()).padStart(2, '0')
  const minute = String(date.getMinutes()).padStart(2, '0')
  return `${hour}:${minute}`
}

const parseMessageContent = (content) => {
  if (!content) return {}
  if (typeof content !== 'string') return content

  try {
    return JSON.parse(content)
  } catch (error) {
    return { content }
  }
}

const normalizeMessage = (messageItem) => {
  const isSelf = Number(messageItem.type) === 1
  const rawMessageTime = Number(messageItem.msgTime)
  const messageTime = rawMessageTime < 100000000000 ? rawMessageTime * 1000 : rawMessageTime
  const messageDate = rawMessageTime ? new Date(messageTime) : new Date()

  return {
    id: String(messageItem.id),
    type: messageItem.msgType,
    msgContent: parseMessageContent(messageItem.msgContent),
    date: formatDate(messageDate),
    time: formatTime(messageDate),
    senderId: isSelf ? messageItem.userId : messageItem.wechatId,
    senderName: isSelf ? messageItem.userName : messageItem.friendName,
    avatar: isSelf ? messageItem.userAvatar : messageItem.friendAvatar,
    isSelf
  }
}

const requestMessageList = ({ direction, cursor, filter = {} }) => {
  const { wechatUserId, wechatId, loginUserId, roomId } = messageParams.value
  const requestData = {
    ...filter,
    paginationId: cursor ? Number(cursor) : undefined,
    swipeDirection: direction === 'up' ? 1 : 0
  }

  if (props.source === 'group') {
    requestData.roomId = roomId
    return getGroupWxChatRecord(requestData)
  }

  requestData.wechatUserId = wechatUserId
  requestData.wechatId = wechatId
  requestData.loginUserId = loginUserId

  return getSingleWxChatRecord(requestData)
}

const resetScrollPosition = (direction, oldScrollHeight) => {
  const scrollElement = scrollBody.value
  if (!scrollElement) return

  if (direction === 'up') {
    scrollElement.scrollTop = scrollElement.scrollHeight - oldScrollHeight
    return
  }

  if (direction === 'initial') {
    scrollElement.scrollTop = scrollElement.scrollHeight
  }
}

const loadMessageList = (direction) => {
  if (direction === 'up' && (!hasMoreUp.value || upLoading.value)) return
  if (direction === 'down' && (!hasMoreDown.value || downLoading.value)) return

  const scrollElement = scrollBody.value
  const oldScrollHeight = scrollElement ? scrollElement.scrollHeight : 0
  const cursorMessage =
    direction === 'down' ? messageList.value[messageList.value.length - 1] : messageList.value[0]
  const loadingRef = direction === 'down' ? downLoading : upLoading

  loadingRef.value = true
  return requestMessageList({
    direction,
    cursor: cursorMessage ? cursorMessage.id : '',
    filter: props.filter
  })
    .then(({ result = {} }) => {
      const nextMessageList = (result.list || []).map((messageItem) =>
        normalizeMessage(messageItem)
      )

      if (direction === 'down') {
        messageList.value = [...messageList.value, ...nextMessageList]
        hasMoreDown.value = Boolean(result.hasMore)
      } else if (direction === 'up') {
        messageList.value = [...nextMessageList, ...messageList.value]
        hasMoreUp.value = Boolean(result.hasMore)
      } else {
        messageList.value = nextMessageList
      }

      nextTick(() => {
        resetScrollPosition(direction, oldScrollHeight)
      })
    })
    .finally(() => {
      loadingRef.value = false
    })
}

const initMessageList = () => {
  messageList.value = []
  locatedMessageId.value = ''
  hasMoreUp.value = true
  hasMoreDown.value = false
  loadMessageList('initial')
}

const handleScroll = () => {
  const scrollElement = scrollBody.value
  if (!scrollElement) return

  if (scrollElement.scrollTop <= 24) {
    loadMessageList('up')
    return
  }

  if (scrollElement.scrollHeight - scrollElement.scrollTop - scrollElement.clientHeight <= 24) {
    loadMessageList('down')
  }
}

const scrollToMessage = (messageId) => {
  const messageRefs = proxy.$refs[`messageItem-${messageId}`]
  const messageElement = Array.isArray(messageRefs) ? messageRefs[0] : messageRefs

  if (messageElement && messageElement.scrollIntoView) {
    messageElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }
  setTimeout(() => {
    nextTick(() => {
      upLoading.value = false
      downLoading.value = false
    })
  }, 1000)
}

const loadTargetMessage = (targetMessage, filter = props.filter) => {
  const targetMessageId = targetMessage && targetMessage.id
  if (!targetMessageId || upLoading.value || downLoading.value) return Promise.resolve()

  upLoading.value = true
  downLoading.value = true
  return Promise.all([
    requestMessageList({
      direction: 'up',
      cursor: targetMessageId,
      filter
    }),
    requestMessageList({
      direction: 'down',
      cursor: targetMessageId,
      filter
    })
  ])
    .then(([{ result: upResult = {} }, { result: downResult = {} }]) => {
      const upMessageList = (upResult.list || []).map((messageItem) =>
        normalizeMessage(messageItem)
      )
      const downMessageList = (downResult.list || []).map((messageItem) =>
        normalizeMessage(messageItem)
      )

      messageList.value = [...upMessageList, ...downMessageList]
      hasMoreUp.value = Boolean(upResult.hasMore)
      hasMoreDown.value = Boolean(downResult.hasMore)
      locatedMessageId.value = targetMessageId
    })
    .then(() => {
      setTimeout(() => {
        nextTick(() => {
          scrollToMessage(targetMessageId)
        })
      }, 100)
    })
    .catch(() => {
      upLoading.value = false
      downLoading.value = false
    })
}

const isCenterMessage = (messageItem) => {
  // 系统消息
  // 拍一拍
  return [ENUM_MESSAGE_TYPE.SYSTEM_MESSAGE.type, ENUM_MESSAGE_TYPE.POKE.type].includes(
    messageItem.type
  )
}

watch(
  () => props.params,
  () => {
    initMessageList()
  },
  { deep: true }
)

watch(
  () => props.filter,
  () => {
    initMessageList()
  },
  { deep: true, immediate: true }
)

defineExpose({
  loadTargetMessage
})
</script>

<style lang="scss" scoped>
.yk-message-core {
  width: 100%;
  height: 100%;
  background: #ffffff;

  &__body {
    box-sizing: border-box;
    height: 100%;
    padding: 16px;
    overflow-y: auto;
  }

  &__item {
    box-sizing: border-box;
    display: flex;
    width: 100%;
    padding-top: 16px;

    &.is-located {
      .yk-message-core__bubble {
        box-shadow: 0 0 0 2px rgba(24, 111, 210, 0.24);
      }
    }

    &.is-self {
      flex-direction: row-reverse;

      .yk-message-core__avatar {
        margin-right: 0;
        margin-left: 12px;
      }

      .yk-message-core__content {
        align-items: flex-end;
      }

      .yk-message-core__meta {
        flex-direction: row;
      }

      .yk-message-core__bubble {
        background: #e8f7ff;
      }
    }
    &.is-center-message {
      flex-direction: row;
      justify-content: center;

      .yk-message-core__avatar,
      .yk-message-core__meta {
        display: none;
      }

      .yk-message-core__content {
        flex-grow: 0;
        align-items: center;
        max-width: 100%;
      }

      .yk-message-core__bubble {
        max-width: 100%;
        padding: 0;
        margin-top: 0;
        background: transparent;
        border-radius: 0;
      }
    }
  }

  &__content {
    display: flex;
    flex-direction: column;
    // flex: 1;
    flex-grow: 1;
    align-items: start;
    // max-width: calc(100% - 48px);
  }

  &__meta {
    display: flex;
    align-items: center;
    font-size: 14px;
    line-height: 20px;
  }

  &__sender {
    display: flex;
    align-items: center;
  }

  &__sender-name {
    color: #646566;
  }

  &__time {
    display: flex;
    margin-left: 8px;
    color: #969799;

    span + span {
      margin-left: 4px;
    }
  }

  &__bubble {
    box-sizing: border-box;
    max-width: calc(100% - 48px);
    // padding: 8px 12px;
    margin-top: 8px;
    background: #f2f3f5;
    border-radius: 4px;
  }

  &__avatar {
    flex-shrink: 0;
    width: 36px;
    height: 36px;
    margin-right: 12px;
    overflow: hidden;
    border-radius: 4px;
  }

  &__tip,
  &__empty {
    padding: 12px 0;
    font-size: 12px;
    line-height: 16px;
    color: #969799;
    text-align: center;
  }

  &__empty {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    font-size: 14px;
  }
}
</style>
